import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { canAccess, FEATURE_LABELS } from '../entitlements';
import { useTheme } from '../ThemeContext';
import { brandColors } from './shared/brandConstants';
import { Lock, ArrowUpRight, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

/**
 * FeatureRoute — Route guard that uses canAccess() to check feature entitlements.
 *
 * Usage in App.js:
 *   <Route path="/emar" element={<FeatureRoute feature="emar"><EmarPage /></FeatureRoute>} />
 *
 * Behaviour:
 *   - Not logged in → redirects to /login with returnUrl
 *   - Logged in but no access → shows upgrade prompt (never /register)
 *   - Logged in + access → renders children
 *   - Regulator hitting pricing → redirect to regulator dashboard
 */
export const FeatureRoute = ({ feature, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { colors } = useTheme();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.warmCream }}>
        <div className="w-8 h-8 border-3 rounded-full animate-spin" style={{ borderColor: colors.greyTrack, borderTopColor: brandColors.primary }} />
      </div>
    );
  }

  // Not logged in → redirect to login with returnUrl
  if (!user) {
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Check entitlement
  const allowed = canAccess({
    role: user.role,
    companyTier: user.companyTier,
    workerTier: user.workerTier,
    feature,
  });

  if (allowed) {
    return children;
  }

  // Not allowed → show upgrade prompt
  return <UpgradePrompt feature={feature} user={user} colors={colors} />;
};

const UpgradePrompt = ({ feature, user, colors }) => {
  const featureLabel = FEATURE_LABELS[feature] || feature;
  const isCompany = user.role === 'company_admin' || user.role === 'company_staff';
  const upgradeUrl = isCompany ? '/pricing' : '/pricing';

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: colors.warmCream }} data-testid="upgrade-prompt">
      <div className="max-w-md w-full p-8 rounded-2xl text-center" style={{ backgroundColor: colors.white, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${brandColors.warning}15` }}>
          <Lock className="w-8 h-8" style={{ color: brandColors.warning }} />
        </div>
        <h2 className="text-xl font-bold mb-2" style={{ color: colors.deepForest }}>
          {featureLabel} requires an upgrade
        </h2>
        <p className="text-sm mb-6" style={{ color: colors.greyText }}>
          {isCompany
            ? `Your current plan (${user.companyTier}) does not include ${featureLabel}. Upgrade to unlock this feature.`
            : `Your current passport (${user.workerTier?.replace(/_/g, ' ')}) does not include ${featureLabel}. Upgrade your passport to unlock this feature.`
          }
        </p>
        <div className="flex flex-col gap-3">
          <a href={upgradeUrl}>
            <Button
              className="w-full h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
              style={{ backgroundColor: brandColors.primary, color: 'white' }}
              data-testid="upgrade-btn"
            >
              View Plans <ArrowUpRight className="w-4 h-4" />
            </Button>
          </a>
          <a href={isCompany ? '/company/dashboard' : '/dashboard'}>
            <Button variant="outline" className="w-full h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};
